/**
 * How the dropship is heard (the rules; `dropshipSound.ts` voices them), on the vehicles' law (`vehicleMix.ts`): quieter
 * and darker with distance, from its side, its own reach — it carries a long way.
 *
 * 67.72 (DECISIONS 215; the owner: "for dropship sound I think it has to roughly align with expected tech in universe.  I'm
 * thinking basically the story that explains all this, interstellar expeditionary ships but also hilux technicals and ar
 * rifles is "200 years in the future, turbofans have gotten pretty good".  Sort if thinking more rumbly / ripply and more
 * akin to a jet engine with aferburner."): the ship's four nacelles are four big turbofans, and its voice (`DROPSHIP_VOICE`)
 * is theirs:
 *   - **the fans**: the broadband roar of the fans' air, its band climbing with the thrust (the run-up's rising roar), a
 *     hiss over it that leads at idle, and the blades' passing buzz under it; the nacelles tilting down to hover darken the
 *     roar (blasting the ground, not the sky behind);
 *   - **the cores**: the turbines' whine under the roar, four engines a hair apart (a slow beat between them), climbing
 *     with the thrust, raised as it closes on the listener and lowered as it leaves (a gentle Doppler);
 *   - **the afterburner**: lit at high thrust (`burnFrom` up: the hover's lift and the climb away), a deep rumble with a
 *     ripping band over it, rippled at a few Hz — the "rumbly / ripply" — and crackle in it. The rumble is felt far off
 *     (it falls slowest, `rumbleReach`); the crackle is close (`crackleReach`, falling twice over). Lighting, a whump;
 *   - **the wash**: the down-blast on the ground under it, a lower churning noise that grows as it hovers low with its
 *     thrust turned down, and is gone in cruise or high.
 * The phases are only these values over time: approach (thrust ~0.7, tilting down, closing; the burner lights as it brakes
 * into the hover), the hover (thrust ~0.9, tilted down, low: the wash, the burner half lit), leaving (thrust 1, climbing,
 * tilting aft, going away: the burner full), the pad's idle (thrust 0.2: the fans' hiss and the cores' whine, no burner),
 * the take-off's spool-up from it (the classic run-up: the whine and the roar climbing, then the burner lighting).
 *
 * Before 67.72, 69.3b's jets (the whine, the roar, the fans' buzz, the wash; orbital-agent-1's draft, design/69.3b-dropship
 * e99be2c6, with 67.68's nacelle tilt), kept for the sheet to compare as sound_sheets/dropshipJets6768.ts. Pure: no audio
 * here, no game state. Unplayed guesses (decision 121): the owner's ear decides.
 */

/**
 * The voice (Hz; levels × `gain`; people):
 *   - the fans' roar band at idle and full, the idle's hiss share, the blades' passing tone at idle and full and its level;
 *   - the cores' whine at idle and full, its level, the four engines' spread (a share of the pitch);
 *   - the afterburner lit from `burnFrom` thrust and full at 1, its rumble's band lit and full, its level, its ripple (Hz),
 *     the crackle's level, how far the rumble carries and how near the crackle is heard;
 *   - loudness (`engines` × it for the engines' parts: the jets' peaks before them), how near it is loud and how far it is heard, the wash's level and the height under which it is heard;
 *   - 67.79, the whine↔ripple dial (`exhaust`, 0 … 1; 0 the voice before, to the byte; 0.6 the owner's pick, "New sound is
 *     good" — B at its start on the sheet): the owner on 69.10's sheet, "I like
 *     the lighter sound, but I like the bell sound the most and almost think the nacelles themselves, even on dropship
 *     should sound a little more bell like, a little less whine and a little more ripple". A lift jet at high thrust is
 *     heard mostly as its exhaust's roar, crackling and fluttering; the fan's whine is a tone heard mostly from in front at
 *     low power. Turned up, the cores' whine falls (× 1 − `whineCut` × the dial), the blades' buzz and the intake's hiss
 *     with it by half that, and the exhaust's roar comes in through the burner's chain at any thrust — its band from
 *     `exhaustLo` to `exhaustHi` with the thrust (above the bells' rumble: they stay the deepest), fluttered at `flutter`
 *     Hz (slower than the bells' ~11), its crackle `exhaustCrackle` (under the bells': they stay the roughest), its
 *     loudness `exhaustLevel` × the fans'. It does not light the burner (no whump).
 */
export const DROPSHIP_VOICE = {
  fanIdle: 420, fanFull: 1100, hiss: 0.5, bladeIdle: 190, bladeFull: 480, blade: 0.14,
  coreIdle: 1350, coreFull: 2600, core: 0.2, spread: 0.006,
  burnFrom: 0.78, rumbleLit: 60, rumbleFull: 120, burner: 0.85, ripple: 4.5, crackle: 0.3, rumbleReach: 160, crackleReach: 30,
  // (the lift fans blast the ground hard in a low hover; four big fans carry further than a truck)
  gain: 0.6, engines: 1.2, reach: 58, range: 1500, wash: 0.7, washBelow: 14,
  // 67.79's dial (0: the voice before; 0.6 the owner's pick) and the exhaust's ripple it brings in
  exhaust: 0.6, whineCut: 0.8, exhaustLevel: 0.75, exhaustLo: 110, exhaustHi: 190, flutter: 6, exhaustCrackle: 0.15,
};

/**
 * Its state as the sound reads it: thrust 0 … 1, the thrusters' tilt (0 aft … 1 down), its height over the ground and its
 * closing speed toward the listener (people, people a second). 69.26: `spool`, the engines turning at all (1 running, the
 * default; 0 off): every part's loudness × it and the turbines' pitches wound down with it — a ship at rest on its pad
 * winds down to silence and back up before it sets out.
 */
export interface DropshipState { thrust: number; tilt: number; heightPeople: number; closing: number; spool?: number }

export interface DropshipHeard {
  /** the fans' roar (its band's centre), the hiss's share of it, and the blades' passing tone */
  fan: { gain: number; hz: number; hiss: number; blade: { gain: number; hz: number } };
  /** the cores' whine: its pitch, the four engines' spread (a share of it) */
  core: { gain: number; pitch: number; spread: number };
  /** the afterburner: how lit (0 … 1), the rumble's loudness and band, its ripple (Hz), the crackle's loudness */
  burner: { lit: number; gain: number; hz: number; ripple: number; crackle: number };
  wash: { gain: number };
  cutoff: number;
  pan: number;
}
const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));
const smooth = (a: number, b: number, x: number) => { const t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };

/** The dropship `people` from the listener, on its `pan` side. Null past its range or with its engines cold (thrust 0). `V`: the voice's numbers (69.10: the lighter's bigger nacelles, audio/lighterMix.ts LIGHTER_JETS). */
export function hearDropship(people: number, pan: number, s: DropshipState, V: typeof DROPSHIP_VOICE = DROPSHIP_VOICE): DropshipHeard | null {
  const thrust = clamp(s.thrust, 0, 1), d = Math.max(0, people), spool = s.spool === undefined ? 1 : clamp(s.spool, 0, 1);
  if (d > V.range || thrust <= 0 || spool <= 0) return null;
  if (spool < 1) return wound(hearDropship(people, pan, { ...s, spool: 1 }, V), spool);
  const near = 1 / (1 + d / V.reach);
  const doppler = clamp(1 + s.closing / 340, 0.8, 1.25);   // (a felt shift: the speed of sound taken as 340 people a second)
  const tilt = clamp(s.tilt, 0, 1), low = clamp(1 - s.heightPeople / V.washBelow, 0, 1) * tilt;
  const lit = smooth(V.burnFrom, 1, thrust), crackleNear = 1 / (1 + d / V.crackleReach), E = V.gain * V.engines;
  // 67.79: the dial — the whine down, the exhaust's ripple in (through the burner's chain, beside the afterburner's)
  const x = clamp(V.exhaust, 0, 1), tone = 1 - V.whineCut * x, soft = 1 - 0.5 * V.whineCut * x;
  const aGain = E * V.burner * lit / (1 + d / V.rumbleReach), aHz = (V.rumbleLit + (V.rumbleFull - V.rumbleLit) * lit) * (1 - 0.2 * tilt), aRipple = V.ripple * (0.8 + 0.4 * lit);
  const xGain = x > 0 ? E * V.exhaustLevel * x * near * (0.25 + 0.75 * thrust) : 0, xHz = (V.exhaustLo + (V.exhaustHi - V.exhaustLo) * thrust) * (1 - 0.2 * tilt), xRipple = V.flutter * (0.8 + 0.4 * thrust);
  const both = aGain + xGain, w = both > 0 ? xGain / both : 0;
  const fanGain = E * near * (0.25 + 0.75 * thrust);
  return {
    fan: {
      gain: fanGain,
      hz: (V.fanIdle + (V.fanFull - V.fanIdle) * thrust * thrust) * (1 - 0.18 * tilt),
      hiss: V.hiss * (1 - 0.75 * thrust) * soft,
      blade: { gain: E * V.blade * near * (0.4 + 0.6 * thrust) * soft, hz: (V.bladeIdle + (V.bladeFull - V.bladeIdle) * thrust) * doppler },
    },
    core: { gain: E * V.core * near * (0.4 + 0.6 * thrust) * tone, pitch: (V.coreIdle + (V.coreFull - V.coreIdle) * thrust) * doppler, spread: V.spread },
    burner: {
      lit, gain: both,
      hz: w > 0 ? aHz * (1 - w) + xHz * w : aHz,
      ripple: w > 0 ? aRipple * (1 - w) + xRipple * w : aRipple,
      crackle: E * V.crackle * lit * crackleNear * crackleNear + (x > 0 ? E * V.exhaustCrackle * x * thrust * thrust * crackleNear * crackleNear : 0),
    },
    wash: { gain: V.gain * V.wash * near * low * thrust },
    cutoff: clamp(9000 * Math.exp(-d / 160), 220, 9000),   // (a jet's roar carries: darker more slowly than a truck's)
    pan: clamp(pan, -1, 1),
  };
}


/** 69.26: `h` with its engines `spool` of the way turning — every loudness × it, the fans', blades' and cores' pitches wound down to 0.3 of theirs at a standstill. */
function wound(h: DropshipHeard | null, spool: number): DropshipHeard | null {
  if (!h) return null;
  const p = 0.3 + 0.7 * spool;
  return {
    ...h,
    fan: { gain: h.fan.gain * spool, hz: h.fan.hz * p, hiss: h.fan.hiss, blade: { gain: h.fan.blade.gain * spool, hz: h.fan.blade.hz * p } },
    core: { ...h.core, gain: h.core.gain * spool, pitch: h.core.pitch * p },
    burner: { ...h.burner, gain: h.burner.gain * spool, crackle: h.burner.crackle * spool },
    wash: { gain: h.wash.gain * spool },
  };
}

/** The sheet's flight, phase by phase: where it is (x across, z toward the listener's front, people) and its state. */
export type DropshipPhase = 'approach' | 'hover' | 'leave';
export const DROPSHIP_PHASES: DropshipPhase[] = ['approach', 'hover', 'leave'];
/** Seconds each phase lasts on the sheet. */
export const PHASE_SECONDS: Record<DropshipPhase, number> = { approach: 12, hover: 8, leave: 10 };

/**
 * At `t` seconds into `phase`, with the hover `standoff` people from the listener: its place relative to the listener
 * (x right, y up, z ahead; people) and its state. Approach: from 900 people out and 60 up, slowing and tilting down to
 * the hover two people up; leave: climbing away and tilting aft, past 900 people.
 */
export function dropshipPhase(phase: DropshipPhase, t: number, standoff: number): { x: number; y: number; z: number; s: DropshipState } {
  const T = PHASE_SECONDS[phase], k = clamp(t / T, 0, 1), sm = k * k * (3 - 2 * k);
  if (phase === 'approach') {
    const along = 900 * (1 - k) * (1 - k);                       // decelerating in
    const speed = (900 * 2 * (1 - k)) / T;
    return { x: -0.4 * along, y: 2 + 58 * (1 - sm), z: standoff + along, s: { thrust: 0.65 + 0.25 * sm, tilt: clamp((k - 0.45) / 0.45, 0, 1), heightPeople: 2 + 58 * (1 - sm), closing: speed } };
  }
  if (phase === 'hover') return { x: 0, y: 2 + 0.2 * Math.sin(t * 1.3), z: standoff, s: { thrust: 0.9, tilt: 1, heightPeople: 2, closing: 0 } };
  const out = 900 * k * k, speed = (900 * 2 * k) / T;
  return { x: 0.3 * out, y: 2 + 10 * sm + 0.06 * out, z: standoff - out, s: { thrust: 1, tilt: 1 - clamp((k - 0.15) / 0.5, 0, 1), heightPeople: 2 + 10 * sm + 0.06 * out, closing: -speed } };
}
